/**
 * Diet-related calculation utilities
 * BMI, BMR, TDEE, and other nutritional calculations
 */

/**
 * Calculate BMI (Body Mass Index)
 * @param weight - Weight in kg
 * @param height - Height in specified unit
 * @param unit - Unit of height ('cm' or 'in')
 * @returns BMI rounded to 1 decimal place
 */
export function calculateBMI(weight: number, height: number, unit: 'cm' | 'in' = 'cm'): number {
  const heightInMeters = unit === 'cm' ? height / 100 : (height * 2.54) / 100
  return Number((weight / (heightInMeters * heightInMeters)).toFixed(1))
}

/**
 * Calculate BMR (Basal Metabolic Rate) using Mifflin-St Jeor Equation
 * @param weight - Weight in kg
 * @param height - Height in cm
 * @param age - Age in years
 * @param gender - Gender ('male', 'female', or 'other')
 * @returns BMR in calories
 */
export function calculateBMR(
  weight: number,
  height: number,
  age: number,
  gender: 'male' | 'female' | 'other'
): number {
  // Mifflin-St Jeor Equation
  if (gender === 'male') {
    return Math.round(10 * weight + 6.25 * height - 5 * age + 5)
  } else if (gender === 'female') {
    return Math.round(10 * weight + 6.25 * height - 5 * age - 161)
  } else {
    // Average for 'other'
    return Math.round(10 * weight + 6.25 * height - 5 * age - 78)
  }
}

/**
 * Calculate TDEE (Total Daily Energy Expenditure)
 * @param bmr - Basal Metabolic Rate
 * @param activityLevel - Activity level
 * @returns TDEE in calories
 */
export function calculateTDEE(
  bmr: number,
  activityLevel: 'sedentary' | 'light' | 'moderate' | 'active' | 'very-active'
): number {
  const multipliers = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    'very-active': 1.9,
  }
  return Math.round(bmr * multipliers[activityLevel])
}

/**
 * Calculate target calories based on goal
 * @param tdee - Total Daily Energy Expenditure
 * @param goalType - Goal type
 * @param weeklyWeightChange - Target weekly weight change in kg (positive for gain, negative for loss)
 * @returns Target calories per day
 */
export function calculateTargetCalories(
  tdee: number,
  goalType: 'weight-loss' | 'muscle-gain' | 'maintenance',
  weeklyWeightChange: number = 0.5
): number {
  if (goalType === 'maintenance') {
    return tdee
  }

  // 7700 calories = 1 kg of body weight
  const caloriesPerKg = 7700
  const dailyCalorieAdjustment = (weeklyWeightChange * caloriesPerKg) / 7

  if (goalType === 'weight-loss') {
    return Math.round(tdee - Math.abs(dailyCalorieAdjustment))
  } else {
    // muscle-gain
    return Math.round(tdee + Math.abs(dailyCalorieAdjustment))
  }
}

/**
 * Calculate target macros based on calories and goal
 * @param targetCalories - Target daily calories
 * @param goalType - Goal type
 * @returns Object with protein, carbs, fat, and fiber targets in grams
 */
export function calculateTargetMacros(
  targetCalories: number,
  goalType: 'weight-loss' | 'muscle-gain' | 'maintenance'
): {
  protein: number
  carbs: number
  fat: number
  fiber: number
} {
  let proteinPercent: number
  let carbsPercent: number
  let fatPercent: number

  switch (goalType) {
    case 'weight-loss':
      proteinPercent = 0.3 // 30% protein
      carbsPercent = 0.4 // 40% carbs
      fatPercent = 0.3 // 30% fat
      break
    case 'muscle-gain':
      proteinPercent = 0.3 // 30% protein
      carbsPercent = 0.45 // 45% carbs
      fatPercent = 0.25 // 25% fat
      break
    case 'maintenance':
    default:
      proteinPercent = 0.25 // 25% protein
      carbsPercent = 0.45 // 45% carbs
      fatPercent = 0.3 // 30% fat
      break
  }

  // Calories per gram: Protein = 4, Carbs = 4, Fat = 9
  const protein = Math.round((targetCalories * proteinPercent) / 4)
  const carbs = Math.round((targetCalories * carbsPercent) / 4)
  const fat = Math.round((targetCalories * fatPercent) / 9)
  const fiber = Math.round(targetCalories / 1000) * 14 // 14g per 1000 calories (recommended)

  return { protein, carbs, fat, fiber }
}
